import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle, XCircle } from "lucide-react";
import type { PlayProps } from "@/types/puzzle";

export default function FillInTheBlankPlay({ data, onAnswer, result }: PlayProps) {
  const { t } = useTranslation();
  const [value, setValue] = useState("");

  const sentence = (data.sentence as string) ?? "";
  const answered = result !== null;
  const isCorrect = result?.correct ?? false;
  const correctAnswer = (result?.solution.correctAnswer as string) ?? "";

  const handleCheck = () => {
    if (!value.trim() || answered) return;
    onAnswer(value);
  };

  const parts = sentence.split("___");

  return (
    <div className="space-y-6 max-w-lg mx-auto">
      <h2 className="text-lg font-medium text-muted-foreground text-center animate-fade-in-up">
        {t("puzzleStudent.fillBlank.heading")}
      </h2>

      <div className="p-5 rounded-lg border border-border bg-card animate-fade-in-up">
        <p className="text-xl font-semibold text-foreground text-center leading-relaxed">
          {parts[0]}
          <span
            className={`inline-block min-w-[80px] rounded-md border-b-2 mx-1 px-2 py-0.5 font-bold transition-colors ${
              answered
                ? isCorrect
                  ? "border-green-500 bg-green-500/10 text-green-700"
                  : "border-red-500 bg-red-500/10 text-red-700"
                : value.trim()
                  ? "border-foreground bg-foreground/5"
                  : "border-muted-foreground/30"
            }`}
          >
            {value || " "}
          </span>
          {parts[1]}
        </p>
      </div>

      <div className="flex gap-2">
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={t("puzzleStudent.fillBlank.placeholder")}
          disabled={answered}
          onKeyDown={(e) => e.key === "Enter" && handleCheck()}
          autoFocus
          className="h-12 text-base font-medium"
        />
        {!answered && (
          <Button onClick={handleCheck} disabled={!value.trim()} className="h-12 px-6">
            {t("puzzleStudent.fillBlank.check")}
          </Button>
        )}
      </div>

      {answered && (
        <div
          className={`flex items-center gap-3 p-4 rounded-lg animate-fade-in-up ${
            isCorrect
              ? "bg-green-500/10 text-green-700"
              : "bg-red-500/10 text-red-700"
          }`}
        >
          {isCorrect ? (
            <CheckCircle className="w-5 h-5 shrink-0 animate-bounce-in" />
          ) : (
            <XCircle className="w-5 h-5 shrink-0 animate-shake" />
          )}
          <span className="text-sm font-semibold">
            {isCorrect
              ? t("puzzleStudent.fillBlank.correct")
              : t("puzzleStudent.fillBlank.correctAnswer", {
                  answer: correctAnswer,
                })}
          </span>
        </div>
      )}
    </div>
  );
}
